import { useCallback, useState } from 'react';
import { useAutoDocExtSuggestionContext } from './AutoDocExtSuggestionProvider';
import {
  readSuggestionStatusFromBucket,
  SuggestionStatus,
  SuggestionStatusBucket,
} from './suggestionStatusStorage';
import usePersistedState from 'hooks/usePersistedState';

export type UseAutoDocExtSuggestionArgs = {
  // Form field name. Used as the per-field key inside the rejection bucket
  // persisted in localStorage.
  fieldName: string;
  // The suggestion text to consider for this field, supplied by the consumer.
  // The hook does not fetch — fetching is the consumer's concern (see
  // useAllAutoDocumentExtractions).
  suggestion: string | null;
  // The current form value for this field. The hook only suggests when this
  // is empty.
  currentValue: unknown;
  // Master gate. The consumer ANDs together its own preconditions (feature
  // flag, has a storage anchor, etc.) and passes the result here.
  enabled: boolean;
  // localStorage key under which the rejection bucket is persisted. Build it
  // with getSuggestionStatusStorageKey (DE1) or
  // getDE2SuggestionStatusStorageKey (DE2) — every reader and writer of a
  // bucket has to agree on its key, so the formats live in one place.
  storageKey: string;
};

export const useAutoDocExtSuggestion = ({
  fieldName,
  suggestion,
  currentValue,
  enabled,
  storageKey,
}: UseAutoDocExtSuggestionArgs) => {
  const { useSuggestions } = useAutoDocExtSuggestionContext();

  const [
    suggestionStatusBucket,
    setSuggestionStatusBucket,
  ] = usePersistedState<SuggestionStatusBucket>(storageKey, {});

  // Acceptance is recorded purely for telemetry — it does not participate
  // in display suppression. Rejection blocks display. Legacy entries stored
  // as `true` are tolerated as `'rejected'` (see suggestionStatusStorage.ts).
  const storedStatus = readSuggestionStatusFromBucket(
    suggestionStatusBucket,
    fieldName
  );
  const isRejectedByUser = storedStatus === 'rejected';

  const [isDismissed, setIsDismissed] = useState(false);

  // Hides the suggestion UI immediately. The consumer is responsible for
  // writing the suggestion value into its form; once that lands, the empty-
  // value gate would also hide the suggestion — but accept() guarantees the
  // hide for the brief window before the consumer's setValue propagates,
  // and makes the intent explicit if a consumer chooses not to write a value.
  // Also records `'accepted'` in the bucket so `useAutoDocExtSuggestionStatus`
  // can surface the decision for telemetry.
  const accept = useCallback(() => {
    setIsDismissed(true);
    setSuggestionStatusBucket((prev) => ({ ...prev, [fieldName]: 'accepted' }));
  }, [fieldName, setSuggestionStatusBucket]);

  const reject = useCallback(() => {
    setIsDismissed(true);
    setSuggestionStatusBucket((prev) => ({ ...prev, [fieldName]: 'rejected' }));
  }, [fieldName, setSuggestionStatusBucket]);

  const hasSuggestion = Boolean(suggestion);
  // Only null / undefined count as empty here. Empty string is treated as
  // "user already interacted with the field"
  const isCurrentValueEmpty =
    currentValue === null || currentValue === undefined;

  const showSuggestion =
    enabled &&
    useSuggestions &&
    hasSuggestion &&
    isCurrentValueEmpty &&
    !isRejectedByUser &&
    !isDismissed;

  const suggestionStatus: SuggestionStatus =
    storedStatus === 'pending' && !isCurrentValueEmpty
      ? 'existing-content'
      : storedStatus;

  return {
    showSuggestion,
    accept,
    reject,
    isRejectedByUser,
    suggestionStatus,
  };
};
